import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { BarChart3, ChevronLeft, ChevronRight, Download } from "lucide-react";
import type { ReportOptions, ReportType } from "@crewledger/shared/schemas";
import { EmptyState, PageHeader, useToast } from "@/components/design";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatCurrency, formatHours, formatPercent } from "@/features/dashboard/utils";
import { cn } from "@/lib/utils";
import { downloadReportCsv, fetchReport, fetchReportOptions } from "./api";
import { useReportPermissions } from "./use-report-permissions";

const PAGE_SIZE = 25;

const REPORT_TABS: { id: ReportType; label: string }[] = [
  { id: "payroll-summary", label: "Payroll Summary" },
  { id: "site-cost", label: "Site Cost" },
  { id: "worker-hours", label: "Worker Hours" },
  { id: "equipment-usage", label: "Equipment Usage" },
  { id: "overtime", label: "Overtime" },
  { id: "exceptions", label: "Exceptions" },
  { id: "geofence-exceptions", label: "Geofence Exceptions" },
  { id: "audit", label: "Audit" },
];

interface ColumnDef {
  key: string;
  label: string;
  render: (row: Record<string, unknown>) => ReactNode;
}

function money(value: unknown): string {
  return value != null && typeof value === "number" ? formatCurrency(value) : "—";
}

function hours(value: unknown): string {
  return value != null && typeof value === "number" ? formatHours(value) : "—";
}

function text(value: unknown): string {
  if (value == null || value === "") return "—";
  return String(value);
}

function col(key: string, label: string, render: ColumnDef["render"]): ColumnDef {
  return { key, label, render };
}

function buildColumns(
  reportType: ReportType,
  canViewPayroll: boolean,
): ColumnDef[] {
  switch (reportType) {
    case "payroll-summary":
      return [
        col("employeeId", "Employee ID", (r) => text(r.employeeId)),
        ...(canViewPayroll ? [col("name", "Name", (r) => text(r.name))] : []),
        col("payPeriodLabel", "Pay period", (r) => text(r.payPeriodLabel)),
        col("regularHours", "Regular", (r) => hours(r.regularHours)),
        col("overtimeHours", "OT", (r) => hours(r.overtimeHours)),
        col("doubleTimeHours", "DT", (r) => hours(r.doubleTimeHours)),
        col("holidayHours", "Holiday", (r) => hours(r.holidayHours)),
        ...(canViewPayroll
          ? [
              col("premiumPay", "Premium", (r) => money(r.premiumPay)),
              col("baseRate", "Base rate", (r) => money(r.baseRate)),
              col("grossPay", "Gross pay", (r) => money(r.grossPay)),
              col("equipmentCost", "Equip. cost", (r) => money(r.equipmentCost)),
            ]
          : []),
        col("notes", "Notes", (r) => text(r.notes)),
      ];
    case "site-cost":
      return [
        col("siteCode", "Site", (r) => text(r.siteCode)),
        col("jobCode", "Job code", (r) => text(r.jobCode)),
        col("costCode", "Cost code", (r) => text(r.costCode)),
        col("laborHours", "Labor hrs", (r) => hours(r.laborHours)),
        ...(canViewPayroll
          ? [
              col("laborGrossPay", "Labor gross", (r) => money(r.laborGrossPay)),
              col("equipmentCost", "Equip. cost", (r) => money(r.equipmentCost)),
              col("totalEstimatedCost", "Total est.", (r) => money(r.totalEstimatedCost)),
            ]
          : []),
        col("equipmentHours", "Equip. hrs", (r) => hours(r.equipmentHours)),
      ];
    case "worker-hours":
      return [
        col("employeeId", "Employee ID", (r) => text(r.employeeId)),
        ...(canViewPayroll ? [col("name", "Name", (r) => text(r.name))] : []),
        col("siteCode", "Site", (r) => text(r.siteCode)),
        col("crew", "Crew", (r) => text(r.crew)),
        col("regularHours", "Regular", (r) => hours(r.regularHours)),
        col("overtimeHours", "OT", (r) => hours(r.overtimeHours)),
        col("doubleTimeHours", "DT", (r) => hours(r.doubleTimeHours)),
        col("holidayHours", "Holiday", (r) => hours(r.holidayHours)),
        col("totalHours", "Total", (r) => hours(r.totalHours)),
        col("entryCount", "Entries", (r) => text(r.entryCount)),
      ];
    case "equipment-usage":
      return [
        col("equipmentId", "Equipment ID", (r) => text(r.equipmentId)),
        col("equipmentName", "Name", (r) => text(r.equipmentName)),
        col("equipmentType", "Type", (r) => text(r.equipmentType)),
        col("siteCode", "Site", (r) => text(r.siteCode)),
        col("workerEmployeeId", "Worker ID", (r) => text(r.workerEmployeeId)),
        col("date", "Date", (r) => text(r.date)),
        col("usageHours", "Hours", (r) => hours(r.usageHours)),
        ...(canViewPayroll
          ? [
              col("hourlyCost", "Hourly cost", (r) => money(r.hourlyCost)),
              col("estimatedCost", "Est. cost", (r) => money(r.estimatedCost)),
            ]
          : []),
        col("notes", "Notes", (r) => text(r.notes)),
      ];
    case "overtime":
      return [
        col("employeeId", "Employee ID", (r) => text(r.employeeId)),
        ...(canViewPayroll ? [col("name", "Name", (r) => text(r.name))] : []),
        col("siteCode", "Site", (r) => text(r.siteCode)),
        col("regularHours", "Regular", (r) => hours(r.regularHours)),
        col("overtimeHours", "OT", (r) => hours(r.overtimeHours)),
        col("doubleTimeHours", "DT", (r) => hours(r.doubleTimeHours)),
        col("overtimePercent", "OT %", (r) =>
          r.overtimePercent != null ? formatPercent(Number(r.overtimePercent)) : "—",
        ),
        ...(canViewPayroll
          ? [
              col("overtimePay", "OT pay", (r) => money(r.overtimePay)),
              col("grossPay", "Gross pay", (r) => money(r.grossPay)),
            ]
          : []),
      ];
    case "exceptions":
      return [
        col("type", "Type", (r) => text(r.type)),
        col("severity", "Severity", (r) => text(r.severity)),
        col("message", "Message", (r) => text(r.message)),
        col("status", "Status", (r) => text(r.status)),
        col("targetType", "Target", (r) => text(r.targetType)),
        col("createdAt", "Created", (r) =>
          r.createdAt ? new Date(String(r.createdAt)).toLocaleString() : "—",
        ),
      ];
    case "geofence-exceptions":
      return [
        col("siteCode", "Site", (r) => text(r.siteCode)),
        col("workerName", "Worker", (r) => text(r.workerName)),
        col("eventType", "Event", (r) => text(r.eventType)),
        col("status", "Status", (r) => text(r.status)),
        col("distanceFromSiteMeters", "Distance (m)", (r) =>
          r.distanceFromSiteMeters != null ? String(Math.round(Number(r.distanceFromSiteMeters))) : "—",
        ),
        col("overrideReason", "Override", (r) => text(r.overrideReason)),
        col("actorName", "Actor", (r) => text(r.actorName)),
        col("createdAt", "When", (r) =>
          r.createdAt ? new Date(String(r.createdAt)).toLocaleString() : "—",
        ),
      ];
    case "audit":
      return [
        col("createdAt", "When", (r) =>
          r.createdAt ? new Date(String(r.createdAt)).toLocaleString() : "—",
        ),
        col("actorName", "Actor", (r) => text(r.actorName ?? r.actorEmail)),
        col("actionLabel", "Action", (r) => text(r.actionLabel)),
        col("targetType", "Target type", (r) => text(r.targetType)),
        col("targetLabel", "Target", (r) => text(r.targetLabel)),
        col("reason", "Reason", (r) => text(r.reason)),
      ];
    default:
      return [];
  }
}

export function ReportsPage() {
  const { canRead, canViewPayroll, isReadOnly } = useReportPermissions();
  const { toast } = useToast();
  const [reportType, setReportType] = useState<ReportType>("payroll-summary");
  const [options, setOptions] = useState<ReportOptions | null>(null);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [serverCanViewPayroll, setServerCanViewPayroll] = useState(canViewPayroll);

  const [payPeriodId, setPayPeriodId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [workerId, setWorkerId] = useState("");
  const [crew, setCrew] = useState("");
  const [equipmentId, setEquipmentId] = useState("");
  const [jobCode, setJobCode] = useState("");
  const [costCode, setCostCode] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const filterParams = useMemo(
    () => ({
      payPeriodId: payPeriodId || undefined,
      siteId: siteId || undefined,
      workerId: workerId || undefined,
      crew: crew || undefined,
      equipmentId: equipmentId || undefined,
      jobCode: jobCode || undefined,
      costCode: costCode || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [payPeriodId, siteId, workerId, crew, equipmentId, jobCode, costCode, dateFrom, dateTo],
  );

  useEffect(() => {
    if (!canRead) return;
    fetchReportOptions()
      .then((res) => { setOptions(res.data); })
      .catch(() =>
        { setOptions({
          payPeriods: [],
          sites: [],
          workers: [],
          crews: [],
          equipment: [],
          jobCodes: [],
          costCodes: [],
        }); },
      );
  }, [canRead]);

  useEffect(() => {
    setPage(1);
  }, [reportType, filterParams]);

  useEffect(() => {
    if (!canRead) return;
    setLoading(true);
    setError(null);
    fetchReport(reportType, { ...filterParams, page, pageSize: PAGE_SIZE })
      .then((res) => {
        setRows(res.data as Record<string, unknown>[]);
        setTotal(res.meta.total ?? res.data.length);
        if (res.meta.canViewPayroll != null) setServerCanViewPayroll(res.meta.canViewPayroll);
      })
      .catch((err: unknown) => {
        setRows([]);
        setTotal(0);
        setError(err instanceof Error ? err.message : "Failed to load report");
      })
      .finally(() => { setLoading(false); });
  }, [canRead, reportType, filterParams, page]);

  const columns = useMemo(
    () => buildColumns(reportType, serverCanViewPayroll),
    [reportType, serverCanViewPayroll],
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (!canRead) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reports"
        description="Payroll, labor, equipment, and audit reports with server-side filtering."
        badge={
          isReadOnly ? (
            <span className="rounded-full bg-navy-deep/5 px-2.5 py-0.5 text-xs font-semibold text-navy-blueprint">
              Read-only
            </span>
          ) : undefined
        }
        actions={
          <Button
            variant="outline"
            onClick={() => {
              void downloadReportCsv(reportType, filterParams).catch((err: unknown) => {
                toast({
                  variant: "error",
                  title: "Export failed",
                  description: err instanceof Error ? err.message : undefined,
                });
              });
            }}
          >
            <Download className="mr-1.5 h-4 w-4" />
            Export CSV
          </Button>
        }
      />

      <div className="flex flex-wrap gap-2">
        {REPORT_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => { setReportType(tab.id); }}
            className={cn(
              "rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors",
              reportType === tab.id
                ? "border-navy-deep bg-navy-deep text-white"
                : "border-input bg-card-white text-muted-foreground hover:bg-bg-light",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid gap-3 rounded-xl border bg-card-white p-4 sm:grid-cols-2 lg:grid-cols-4">
        <select
          value={payPeriodId}
          onChange={(e) => { setPayPeriodId(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All pay periods</option>
          {options?.payPeriods.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
        <select
          value={siteId}
          onChange={(e) => { setSiteId(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All sites</option>
          {options?.sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.code} — {s.name}
            </option>
          ))}
        </select>
        <select
          value={workerId}
          onChange={(e) => { setWorkerId(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All workers</option>
          {options?.workers.map((w) => (
            <option key={w.id} value={w.id}>
              {w.employeeId} — {w.name}
            </option>
          ))}
        </select>
        <select
          value={crew}
          onChange={(e) => { setCrew(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All crews</option>
          {options?.crews.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={equipmentId}
          onChange={(e) => { setEquipmentId(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All equipment</option>
          {options?.equipment.map((e) => (
            <option key={e.id} value={e.id}>
              {e.equipmentId} — {e.name}
            </option>
          ))}
        </select>
        <select
          value={jobCode}
          onChange={(e) => { setJobCode(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All job codes</option>
          {options?.jobCodes.map((j) => (
            <option key={j} value={j}>
              {j}
            </option>
          ))}
        </select>
        <select
          value={costCode}
          onChange={(e) => { setCostCode(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All cost codes</option>
          {options?.costCodes.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <div className="flex gap-2">
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); }}
            className="h-9"
            aria-label="Date from"
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); }}
            className="h-9"
            aria-label="Date to"
          />
        </div>
      </div>

      {error ? (
        <EmptyState icon={BarChart3} title="Could not load report" description={error} />
      ) : loading ? (
        <div className="h-64 animate-pulse rounded-xl border bg-card-white" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={BarChart3}
          title="No rows match your filters"
          description="Adjust filters or select a different report type."
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border bg-card-white shadow-[var(--shadow-card)]">
            <table className="w-full min-w-[800px] text-sm">
              <thead>
                <tr className="border-b bg-bg-light text-left text-xs uppercase tracking-wider text-muted-foreground">
                  {columns.map((col) => (
                    <th key={col.key} className="px-4 py-3 font-medium">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={String(row.id ?? idx)} className="border-b last:border-0 hover:bg-bg-light/50">
                    {columns.map((col) => (
                      <td key={col.key} className="px-4 py-3">
                        {col.render(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between rounded-xl border bg-card-white px-4 py-3 text-sm">
            <p className="text-muted-foreground">
              {total.toLocaleString()} row{total === 1 ? "" : "s"} · page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => { setPage((p) => Math.max(1, p - 1)); }}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => { setPage((p) => p + 1); }}
              >
                Next
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
